/**
 * Shared contracts between @analytics/web and @analytics/api.
 * Keep this the single source of truth for the API response shapes.
 */

// ── auth / session ──

/** RBAC role. admin + board_member are unscoped; sales_manager is scoped; receiver is unscoped read. */
export type Role = "admin" | "board_member" | "sales_manager" | "receiver";

export const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  board_member: "Board member",
  sales_manager: "Sales manager",
  receiver: "Receiver",
};

export interface SessionInfo {
  email: string;
  name: string;
  /** dashboards this account may see (code-level allowlist is the gate for the whole app) */
  dashboards: DashboardId[];
  /** this account's role (from the config DB; fail-closed if unavailable) */
  role: Role;
  /** set when an admin/board viewer is impersonating a sales manager */
  impersonating?: { email: string; name: string } | null;
  /** effective-viewer scope summary; null = unrestricted (sees everything) */
  scope?: { rvaCount: number } | null;
  /** convenience flags derived server-side from role */
  canImpersonate: boolean;
  canEditRoles: boolean;
  canEditScopes: boolean;
}

/** A sales-manager's data scope. Empty/absent dimensions impose no limit on that dimension. */
export interface SmScope {
  /** RVA account ids (lead_distribute_job_result.user) the manager may see */
  rvas?: string[];
  flowIds?: number[];
  jobIds?: string[];
  groupIds?: number[];
}

/** An impersonation target shown to admin/board. */
export interface ImpersonationTarget {
  email: string;
  name: string;
  role: Role;
  /** RVA count if the target is scoped; 0 when unscoped (sees everything) */
  rvaCount: number;
  /** true when the target's view is restricted (scoped) */
  scoped: boolean;
}

/** One row of the admin Users management table. */
export interface AdminUserRow {
  email: string;
  name: string;
  role: Role;
  /** the STORED scope (what admin edits); absent/empty rvas → dynamic team scope is used */
  scope?: SmScope | null;
  /** effective RVA count in force (dynamic team or pinned list); 0 when unscoped */
  effectiveRvaCount?: number;
  /** true when the RVA scope is derived live from their team (sales_manager, no pinned rva list) */
  dynamicScope?: boolean;
  /** true when this user's data view is restricted by a scope (any role) */
  scoped?: boolean;
  /** true when this email is in the code/env sign-in allowlist (can't be removed from the UI) */
  fromCode?: boolean;
}

export interface AdminUsers {
  me: string;
  myRole: Role;
  canEditRoles: boolean;
  canEditScopes: boolean;
  rows: AdminUserRow[];
}

export type DashboardId = "lead-distribution";

export interface AuthConfig {
  googleConfigured: boolean;
  devLogin: boolean;
  allowedDomain: string;
}

/** One row of the access list (who may use the portal + which dashboards). */
export interface AccessEntry {
  email: string;
  dashboards: DashboardId[];
}

export interface AccessList {
  /** the signed-in viewer */
  me: string;
  entries: AccessEntry[];
}

// ── lead-distribution dashboard ──

/** Filters shared by every lead-distribution query (all optional; omitted = all). */
export interface LeadDistFilters {
  /** inclusive epoch millis lower bound on created_time */
  from?: number;
  /** inclusive epoch millis upper bound on created_time */
  to?: number;
  /** group_distribution.id — multi-select; matches any */
  groupIds?: number[];
  /** project id or alias (matched against distribute_criteria JSON) — multi-select; matches any */
  projects?: string[];
  /** RVA account id (lead_distribute_job_result.user) — multi-select; matches any */
  rvas?: string[];
  /** distribution job id (lead_distribute_job.job_id) — "lượt phân phối" — multi-select; matches any */
  jobIds?: string[];
  /** distribution flow id (lead_flow.id / result.flow_id) — "luồng phân phối (chiến dịch)" — multi-select; matches any */
  flowIds?: number[];
  /** care-status group to narrow by (chua-chuyen-doi | trung-tinh | tich-cuc | tieu-cuc) */
  careGroup?: string;
  /** a single contact_status value to narrow by (drill-down on a status chip) */
  careStatus?: string;
}

/** Headline KPI counts (deduped per job_id+lead_id, latest state). */
export interface LeadDistSummary {
  received: number;
  assigned: number;
  handled: number;
  revoked: number;
  /** derived rates in [0,1] */
  assignedRate: number;
  handledRate: number;
  revokedRate: number;
  /** data freshness: max(created_time) in the current filter, epoch millis (null = no rows) */
  latestAt: number | null;
}

/** One ordered step of a funnel: count + % of the funnel head. */
export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  /** share of the funnel head (step[0]) in [0,1] */
  pctOfHead: number;
  /** conversion from the previous step in [0,1] (1 for the head) */
  pctOfPrev: number;
}

export interface LeadDistFunnel {
  steps: FunnelStep[];
}

/** A state-to-state transition count (how leads move between lifecycle states). */
export interface StateTransition {
  from: string;
  to: string;
  count: number;
  /** share of all transitions out of `from` in [0,1] */
  pct: number;
}

export interface LeadDistTransitions {
  /** distinct lifecycle states present, in canonical order */
  states: string[];
  transitions: StateTransition[];
}

/** Filter dropdown options. */
export interface LeadDistFilterOptions {
  groups: { id: number; name: string; count: number }[];
  /** RVA id → full name (from rever-profile.user); name falls back to the id */
  rvas: { user: string; name: string; count: number }[];
  projects: { id: string; name: string; count: number }[];
  /** distribution jobs ("lượt phân phối") */
  jobs: { id: string; name: string; count: number }[];
  /** distribution flows / campaigns ("luồng phân phối") */
  flows: { id: number; name: string; count: number }[];
}

// ── lead-distribution REPORT (care-status, per image: "Báo cáo phân phối lead") ──

/**
 * The lead care-status report. Care-status = the sales-updated `contact_status`
 * on the personal-contact record (rever_contact.personal_contact), joined by p_cid.
 * Enum + Vietnamese labels mirror MigrateContactStatus.scala.
 */
export interface LeadDistReport {
  /** Lead đã phân phối — all latest rows in the filter */
  distributed: number;
  /** Đã được tương tác — care-status moved off "chưa liên hệ" */
  interacted: number;
  /** chưa tương tác — still "chưa liên hệ" / no status */
  notInteracted: number;
  /** Đã chuyển đổi — same population as interacted (đã đổi khỏi "Chưa liên hệ") */
  converted: number;
  /** Tích cực — agree_consult/hot_prospect/have_met/negotiate/under_contract/won */
  positive: number;
  /** Từ chối tư vấn — refuse_consult */
  refuseConsult: number;
  /** Đã thu hồi — leads currently revoked (latest status = 11) */
  revoked: number;
  /** tổng N lần thu hồi — total revoke events across the filtered leads */
  revokeEvents: number;
  /** per care-status breakdown (for "Tình trạng lead" chips) */
  statuses: CareStatusBucket[];
  /** rolled up into the 4 display groups (for the stacked bar) */
  groups: CareGroupBucket[];
  latestAt: number | null;
}

export interface CareStatusBucket {
  /** internal contact_status value (e.g. refuse_consult) */
  key: string;
  label: string;
  /** one of: chua-chuyen-doi | trung-tinh | tich-cuc | tieu-cuc */
  group: string;
  count: number;
  pct: number;
}

export interface CareGroupBucket {
  key: string;
  label: string;
  count: number;
  pct: number;
}

/** One agent row of the "Chi tiết theo RVA" table. */
export interface RvaDetailRow {
  user: string;
  name: string;
  /** best-effort core-team / receiver-group subtitle */
  teamLabel: string | null;
  distributed: number;
  /** Tương tác (engagement-based): distributed − revoked, consistent with Thu hồi */
  interacted: number;
  /** Chưa tương tác = revoked (worker's "not interacted in 72h" verdict) */
  notInteracted: number;
  interactRate: number;
  revoked: number;
  /** Đổi trạng thái — leads whose care-stage (contact_status) was moved off "Chưa liên hệ" */
  statusChanged: number;
}

export interface RvaDetail {
  rows: RvaDetailRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** One lead row for the drill-down table. */
export interface LeadDistRow {
  jobId: string;
  jobName: string | null;
  leadId: number | null;
  rva: string | null;
  group: string | null;
  project: string | null;
  receivedAt: number | null;
  status: string;
  contactStatus: string;
  crmStage: string | null;
}

export interface LeadDistDrilldown {
  rows: LeadDistRow[];
  total: number;
}

/** Whether a given lead-distribution query served real DB data or fixtures. */
export interface SourceMeta {
  live: boolean;
  note?: string;
}
